import type { OperatorTabletWorkspace } from "./useOperatorTabletWorkspace";
import { CasePackagingSheet } from "../CasePackagingSheet";
import { HistoricalProductScrapSheet } from "../HistoricalProductScrapSheet";
import { OperatorMaterialScrapSheet } from "../OperatorMaterialScrapSheet";
import { OperatorMaterialsSheet } from "../OperatorMaterialsSheet";
import { operatorDraftKey } from "../operator-drafts";
import { TouchDowntimeSheet } from "../TouchDowntimeSheet";
import { TouchQuantitySheet } from "../TouchQuantitySheet";
import { PendingQuantityWrites } from "../PendingQuantityWrites";
import { TouchSheet as Sheet } from "../TouchSheet";
import { amount, operatorNumber, operatorAmount } from "./quantity-format";
import { DowntimeConfirmSheet, OperatorComplaintSheet, ReasonSheet } from "./OperatorActionSheets";
import { CompletionCheckSheet } from "./CompletionCheckSheet";
import { entryLabel } from "../production-entry-label";

export function OperatorTabletDialogs({ scrapSheet, selected, setScrapSheet, setEntryType, reference, terminalContext, operationTerminalCode, user, historicalScrapSaved, materialScrapSaved, materialsOpen, setMaterialsOpen, casePackagingOpen, setCasePackagingRunId, quantityUnits, packagingSaved, pendingWritesOpen, setPendingWritesOpen, quantityWrites, entryType, openDowntime, visibleDowntimeReason, saving, submitQuantity, machineCountOpen, setMachineCountOpen, usesCompletedCases, liveCounter, machineBoxes, machineUnit, totals, machineComparison, downtimeConfirmOpen, setDowntimeConfirmOpen, confirmDowntime, complaintOpen, setComplaintOpen, submitComplaint, visibleDowntimeReasonRecord, visibleDowntimeReasonKey, visibleDowntimeDraftKey, recentReasonIds, downtimeReasonDrafts, changeReasonDraft, deferMissingReasons, assignCompletedReason, correctionEntry, setCorrectionEntry, submitCorrection, completionOpen, completionCheck, setCompletionOpen, setCompletionCheck, submitCompletion }: OperatorTabletWorkspace["dialogs"]) {
  return (
    <>
      {scrapSheet === "CHOICE" && selected ? <Sheet title="Iskarta kaydı" close={() => setScrapSheet(null)}><div className="touch-form">
        <p>{selected.status === "COMPLETED" ? "Tamamlanmış işe ek kayıt için vardiya amiri onayı gerekir." : "Ürün veya reçetedeki malzeme kaybını seçin."}</p>
        <button type="button" onClick={() => { if (selected.status === "COMPLETED") setScrapSheet("PRODUCT"); else { setScrapSheet(null); setEntryType("SCRAP"); } }}>Ürün ıskartası</button>
        <button type="button" onClick={() => setScrapSheet("MATERIAL")}>Malzeme ıskartası ve kayıtları</button>
      </div></Sheet> : null}
      {scrapSheet === "PRODUCT" && selected && reference ? <HistoricalProductScrapSheet key={`${selected.id}:${terminalContext.session?.id}`} run={selected} terminalCode={operationTerminalCode} sessionId={terminalContext.session?.id ?? ""} operatorId={terminalContext.session?.operatorId ?? ""} preview={Boolean(user.preview)} reasons={reference.reasons} draftKey={operatorDraftKey(operationTerminalCode, terminalContext.session?.operatorId ?? "", terminalContext.session?.id ?? "", selected.id, "historical-scrap")} close={() => setScrapSheet(null)} onSaved={historicalScrapSaved} /> : null}
      {scrapSheet === "MATERIAL" && selected && reference ? <OperatorMaterialScrapSheet key={`${selected.id}:${terminalContext.session?.id}`} run={selected} terminalCode={operationTerminalCode} sessionId={terminalContext.session?.id ?? ""} operatorId={terminalContext.session?.operatorId ?? ""} preview={Boolean(user.preview)} reasons={reference.reasons} close={() => setScrapSheet(null)} onSaved={materialScrapSaved} /> : null}
      {materialsOpen && selected ? <OperatorMaterialsSheet runId={selected.id} terminalCode={operationTerminalCode} sessionId={terminalContext.session?.id ?? ""} close={() => setMaterialsOpen(false)} /> : null}
      {casePackagingOpen && selected ? <CasePackagingSheet key={`${selected.id}:${terminalContext.session?.id ?? ""}`} run={selected} terminalCode={operationTerminalCode} sessionId={terminalContext.session?.id ?? ""}
        close={() => setCasePackagingRunId(null)} onContractChanged={quantityUnits.retry} onSaved={packagingSaved} /> : null}
      {pendingWritesOpen ? <Sheet title="Bekleyen kayıtlar" close={() => setPendingWritesOpen(false)}>{!quantityWrites.items.length && !quantityWrites.storageError ? <p className="operator-pending-empty" role="status">Bekleyen kayıt kalmadı.</p> : null}<PendingQuantityWrites items={quantityWrites.items} storageError={quantityWrites.storageError} canRetry={quantityWrites.canRetry} retry={quantityWrites.retry} verify={quantityWrites.verify} dismissRejected={quantityWrites.dismissRejected} /></Sheet> : null}
      {entryType && selected && reference && !openDowntime && !visibleDowntimeReason ? (
        <TouchQuantitySheet
          type={entryType}
          productCode={selected.productCode}
          productName={selected.productName}
          draftKey={operatorDraftKey(operationTerminalCode, terminalContext.session?.operatorId ?? "", terminalContext.session?.id ?? "", selected.id, `quantity:${entryType}`)}
          preview={Boolean(user.preview)}
          key={`${selected.id}:${entryType}`}
          unit={selected.unit}
          unitContract={quantityUnits.contract}
          unitsLoading={quantityUnits.loading}
          unitsError={quantityUnits.error}
          retryUnits={quantityUnits.retry}
          reasons={reference.reasons}
          saving={saving}
          close={() => setEntryType(null)}
          submit={submitQuantity}
        />
      ) : null}
      {machineCountOpen && selected && !openDowntime && !visibleDowntimeReason ? (
        <Sheet title="Makine üretimi" close={() => setMachineCountOpen(false)}>
          <div className="touch-form">
            <p>{usesCompletedCases ? "Tamamlanan koli sayısı doğrudan makinenin koli sayacından alınır." : "Bu işin üretimi PLC sayacından otomatik kaydedilir. Aynı miktarı elle girmeyin."}</p>
            {usesCompletedCases ? <dl className="completion-check-grid">
              <div><dt>Bu işte tamamlanan koli</dt><dd>{liveCounter.completedCases.runValue == null ? "Başlangıç doğrulanamadı" : `${operatorNumber(liveCounter.completedCases.runValue, 0)} koli`}</dd></div>
              <div><dt>Makine toplamı</dt><dd>{liveCounter.completedCases.rawValue == null ? "Ölçüm bekleniyor" : `${operatorNumber(liveCounter.completedCases.rawValue, 0)} koli`}</dd></div>
            </dl> : <dl className="completion-check-grid">
              <div><dt>Bu işte sayılan</dt><dd>{machineBoxes == null ? "Ölçüm bekleniyor" : `${operatorNumber(machineBoxes)} ${machineUnit}`}</dd></div>
              <div><dt>Kaydedilen üretim</dt><dd>{operatorAmount(totals.PRODUCTION, selected.unit)}</dd></div>
            </dl>}
            <p role="status">{usesCompletedCases ? liveCounter.completedCases.label : machineComparison ? liveCounter.telemetry.countLabel : liveCounter.status.label}</p>
            <footer className="touch-form-actions"><button type="button" className="touch-cancel" onClick={() => setMachineCountOpen(false)}>Kapat</button></footer>
          </div>
        </Sheet>
      ) : null}
      {downtimeConfirmOpen && selected && !openDowntime && !visibleDowntimeReason ? (
        <DowntimeConfirmSheet
          machineConfigured={liveCounter.counter?.configured === true}
          lineName={selected.lineName}
          saving={saving}
          close={() => setDowntimeConfirmOpen(false)}
          confirm={confirmDowntime}
        />
      ) : null}
      {complaintOpen && selected && !openDowntime && !visibleDowntimeReason ? (
        <OperatorComplaintSheet
          saving={saving}
          close={() => setComplaintOpen(false)}
          submit={submitComplaint}
        />
      ) : null}
      {visibleDowntimeReason && visibleDowntimeReasonRecord?.endedAt && visibleDowntimeReasonKey && visibleDowntimeDraftKey && reference ? (
        <TouchDowntimeSheet
          key={visibleDowntimeDraftKey}
          target={visibleDowntimeReason}
          recentReasonIds={recentReasonIds}
          endedAt={visibleDowntimeReasonRecord.endedAt}
          terminalCode={operationTerminalCode}
          sessionId={terminalContext.session?.id ?? ""}
          preview={Boolean(user.preview)}
          draftKey={visibleDowntimeDraftKey}
          reasons={reference.reasons}
          saving={false}
          draft={downtimeReasonDrafts[visibleDowntimeReasonKey] ?? { reasonCodeId: "", customMode: false, customReason: "", note: "" }}
          onDraftChange={changeReasonDraft}
          close={deferMissingReasons}
          submit={assignCompletedReason}
        />
      ) : null}
      {correctionEntry && selected && !openDowntime && !visibleDowntimeReason ? (
        <ReasonSheet
          title="Hatalı kaydı geri al"
          text={`${entryLabel(correctionEntry.type)} · ${amount(correctionEntry.quantity, correctionEntry.unit)}`}
          action="Kaydı geri al"
          saving={saving}
          close={() => setCorrectionEntry(null)}
          submit={submitCorrection}
        />
      ) : null}
      {completionOpen && selected && completionCheck && !openDowntime && !visibleDowntimeReason ? (
        <CompletionCheckSheet
          check={completionCheck}
          unit={selected.unit}
          machineUnit={machineUnit}
          saving={saving}
          close={() => { setCompletionOpen(false); setCompletionCheck(null); }}
          submit={submitCompletion}
        />
      ) : null}
    </>
  );
}
